// import { first } from 'lodash'
import { useRouter } from 'next/router'
import { forwardRef, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
    AddOfferField,
    isCYOProduct,
    shouldChooseCYOOffer,
    isRosProduct,
    isServiceProductBySlug,
    isSkipTrace,
    postageFieldName,
    returnMailAddOnField,
    rosField,
    rosOfferPercentage,
    undelivarblesField,
} from '../../helpers/slug'
import {
    _CUSTOM_OFFER__KEY,
    formatPrice,
    mimetypes,
    stopEscKey,
} from '../../helpers/utils'
import { CsvStore } from '../../state/CsvStore'
import { ModalStore } from '../../state/ModalStore'
import { Mapping } from '../Mapping'
import { MappingSkipTracing } from '../MappingSkipTracing'
import ConfirmCSVRemove from '../Modals/ConfirmCSVRemove'
import ROSOffersModal from '../Modals/ROSOffersModal'
import { UploadStats } from '../Product/UploadStats'
import { ShowField } from '../Product/ShowField'
import { levels } from '../../helpers/ros'
// import { CSV } from '../../helpers/csvparse'

/**
 * @ISSUES
 * once we delete a leadFile, upload does not work.
 */

/**
 * @NOTE
 * When you upload multiple files duplicates are removed
 * now if you delete one file, only which were unique one left in another file, assuming another file as added after
 * will be shown as quantity, there why if second file is just part of first file, you end up having 0 quantity.
 */

const MappingFileUpload = forwardRef(({ shouldReset, ...props }, ref) => {
    const { item, errors, name, setValue, removeCsv, _product } = props
    const { totalInvalids, getValues, watch } = props
    const { label } = item
    const hideListAddOns = Boolean(props.hideListAddOns)

    const addModal = ModalStore((state) => state.add)
    const addCsv = CsvStore((state) => state.add)
    const csvFiles = CsvStore((state) => state.files)

    const [localErrors, setLocalErrors] = useState([])
    const [files, setFiles] = useState([])
    const [rosOffer, setRosOffer] = useState(null)
    const [showReturneMailAddOn, setShowReturneMailAddOn] = useState(false)

    const [shouldShowChooseOffer, setShouldShowChooseOffer] = useState(false)

    const addOfferValue = watch(AddOfferField.name)
    const selectedOfferPercentage = watch?.(rosOfferPercentage.name)
    const watchedFiles = watch?.(item.name)

    const router = useRouter()
    const product = _product || router.query.slug // _product is for reuploading

    const isCYO = isCYOProduct({ slug: product })
    const cyoOfferFields = isCYO
        ? [
              'Touches',
              ...Array.from({ length: 6 }, (_, index) => [
                  `Mailer Type ${index + 1}`,
                  `Mailer Type ${index + 1} ${AddOfferField.name}`,
              ]).flat(),
          ]
        : []
    const cyoOfferValues = watch(cyoOfferFields)
    const cyoNeedsOffer =
        isCYO &&
        shouldChooseCYOOffer(
            Object.fromEntries(
                cyoOfferFields.map((key, index) => [
                    key,
                    cyoOfferValues[index],
                ]),
            ),
        )

    const handleMapping = (freshFiles) => {
        for (const file of freshFiles) {
            isSkipTrace(router.query)
                ? addModal(
                      <MappingSkipTracing onFinish={onFinish} file={file} />,
                  )
                : addModal(<Mapping onFinish={onFinish} file={file} />)
        }
    }

    const getFreshFiles = (newFiles) => {
        let freshFiles = []
        let filePaths = files.map((file) => file.path)
        newFiles.forEach((file) => {
            if (!filePaths.includes(file.path)) {
                freshFiles.push(file)
            }
        })
        return freshFiles
    }

    const deleteFile = (name) => {
        addModal(<ConfirmCSVRemove onConfirm={() => handleDelete(name)} />)
    }

    const updateFieldValue = (files) => {
        files.length > 0 ? setValue(item.name, files) : setValue(item.name, [])
    }

    const handleDelete = (name) => {
        removeCsv(name, product)
        setFiles((state) => {
            const files = state.filter((file) => file.path !== name)
            updateFieldValue(files)
            return files
        })
    }

    const handledropFiles = (acceptedFiles) => {
        // We will only show mapping fields for files which has not been uploaded already (file.path)
        const freshFiles = getFreshFiles(acceptedFiles)
        freshFiles.length > 0 && handleMapping(freshFiles)
    }

    const onFinish = ({ file, ...props }) => {
        setFiles((state) => {
            const files = [...state, file]
            updateFieldValue(files)
            return files
        })

        const name = file.name

        // to downloading the file for testing
        // CSV.downloadCSVFile(props.data.valids)

        addCsv({
            product,
            name,
            ...props,
        })
    }

    const onDropAccepted = (acceptedFiles) => {
        setLocalErrors([])
        handledropFiles(acceptedFiles)
    }

    const onDropRejected = (files) => {
        let errorsArray = []
        files.map(({ errors }) => {
            if (errors && !errorsArray.includes(errors[0]))
                errorsArray.push(errors[0])
        })
        setLocalErrors(errorsArray)
    }

    const { getRootProps, getInputProps } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept: mimetypes[item.allowed_types],
    })

    const handleOfferSelection = ({ offer, offerPercentage }) => {
        const isCustomOffer = offer === _CUSTOM_OFFER__KEY
        const value = isCustomOffer
            ? `${_CUSTOM_OFFER__KEY} Offer`
            : `Offer ${offer}`

        setValue(rosField.name, value)
        setValue(rosOfferPercentage.name, offerPercentage)
        setRosOffer(offer)
    }

    useEffect(() => {
        document.addEventListener('keydown', stopEscKey)
        return document.removeEventListener('keydown', stopEscKey)
    }, [])

    useEffect(() => {
        setFiles([])
    }, [shouldReset])

    useEffect(() => {
        const currentValue = Array.isArray(watchedFiles)
            ? watchedFiles
            : getValues?.(item.name)

        if (Array.isArray(currentValue) && currentValue.length > 0) {
            setFiles(currentValue)
        } else {
            setFiles([])
        }
    }, [getValues, item.name, watchedFiles])

    useEffect(() => {
        if (!getValues()) return
        const postage = getValues(postageFieldName)
        if (postage && String(postage).includes('First Class')) {
            setShowReturneMailAddOn(true)
        }
    }, [getValues()])

    // useEffect(() => {
    //     const subscription = watch((value, { name }) => {
    //         const fieldName = name
    //         const fieldValue = value[name]

    //         if (fieldName?.includes(AddOfferField.name)) {
    //             setShouldShowChooseOffer(
    //                 AddOfferField.paidOption.includes(fieldValue),
    //             )
    //         }
    //     })
    //     return () => {
    //         subscription.unsubscribe()
    //         setShouldShowChooseOffer(false)
    //     }
    // }, [])

    useEffect(() => {
        if (addOfferValue) {
            setShouldShowChooseOffer(
                AddOfferField.paidOption.includes(addOfferValue),
            )
        } else if (isRosProduct(product)) {
            setShouldShowChooseOffer(true)
        } else {
            setShouldShowChooseOffer(false)
        }
    }, [addOfferValue, files.length])

    const openOfferModal = () => {
        addModal(<ROSOffersModal onFinish={handleOfferSelection} />)
    }

    const selectedOfferLabel = (() => {
        if (!rosOffer) return ''

        if (rosOffer === _CUSTOM_OFFER__KEY) {
            return selectedOfferPercentage
                ? `${selectedOfferPercentage}% of value`
                : 'Custom offer selected'
        }

        const percentage = parseFloat(formatPrice(levels[rosOffer], 2)) * 100
        return `${percentage}% of value`
    })()

    const listFiles = files.map((file) => {
        const displayName =
            file?.name ||
            file?.path ||
            (typeof file?.url === 'string' ? file.url.split('/').pop() : '')
        return (
            <li
                className="mt-2 flex items-center gap-[9px] rounded-[9px] border border-[#E1DACE] bg-white px-3 py-[9px] text-[13.5px] text-[#1A1D23]"
                key={file.path || file.name || file.url}>
                <span className="font-bold text-[#2E9E6B]">✓</span>
                <span className="min-w-0 flex-1 truncate">{displayName}</span>
                <button
                    type="button"
                    className="ml-auto border-none bg-transparent text-[15px] font-semibold text-[#9AA0A6] transition-colors hover:text-[#6B7280]"
                    onClick={() => deleteFile(file.path || file.name)}>
                    ×
                </button>
            </li>
        )
    })

    return (
        <>
            {/* <div className="p-2 bg-yellow-100">
                <ShowField
                    shouldReset={shouldReset}
                    item={{ ...AddOfferField }}
                    setValue={setValue}
                    errors={errors}
                    getValues={getValues}
                />
            </div> */}

            <div {...getRootProps()}>
                <div className="space-y-2 text-olm ">
                    <input
                        data-input-id="file-upload"
                        ref={ref}
                        {...getInputProps()}
                    />
                    <label
                        htmlFor={label}
                        className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                        {label}
                    </label>
                    <div className="mt-1 sm:mt-0 sm:col-span-2">
                        <div
                            className={`
            ${errors?.[name] ? 'border-red-500' : 'border-gray-300 text-olm'}
            max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md`}>
                            <div className="space-y-1 text-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="mx-auto h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                                </svg>
                                <div className="flex text-sm text-olm">
                                    <div className="relative underline cursor-pointer text-olm rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 ">
                                        <span>Upload a file</span>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            className="sr-only"
                                        />
                                    </div>
                                    <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">
                                    CSV Files only
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ul>{listFiles}</ul>

            {/* <div className="mt-4 flex justify-end">
                        <Primary onClick={
                            () => {
                             addModal(<ROSOffersModal onFinish={handleOfferSelection} />)
                            }
                        }> Get Offers </Primary>
                    </div> */}

            {files.length > 0 &&
                (isCYO ? cyoNeedsOffer : shouldShowChooseOffer) && (
                    <div className="mt-4">
                        {rosOffer ? (
                            <div className="flex items-center justify-between gap-4 rounded-[16px] border border-[#F6D9C2] bg-[#FBF3EA] px-4 py-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#ED5C2F] text-white">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            aria-hidden="true">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[14px] font-bold leading-[1.35] text-[#1A1D23]">
                                            {selectedOfferLabel} selected
                                        </div>
                                        <div className="text-[12.5px] leading-[1.45] text-[#6B7280]">
                                            Printed on each mailer
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    data-button-id="choose-ros-offer"
                                    onClick={openOfferModal}
                                    className="inline-flex flex-shrink-0 items-center justify-center rounded-full border border-[#F6D9C2] bg-white px-4 py-2 text-[13.5px] font-semibold text-[#ED5C2F] transition-colors hover:bg-[#FDF4EC] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F28230]/15">
                                    Change offer value
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                data-button-id="choose-ros-offer"
                                onClick={openOfferModal}
                                className="inline-flex w-full items-center justify-center rounded-full bg-[linear-gradient(90deg,#ED5C2F,#F28230)] px-[22px] py-3 text-[14.5px] font-semibold text-white shadow-[0_6px_16px_rgba(237,92,47,0.28)] transition-[transform,box-shadow,filter] duration-150 hover:shadow-[0_10px_22px_rgba(237,92,47,0.34)] active:translate-y-[1px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F28230]/15">
                                <span className="mr-2">Choose your offer</span>
                                <span aria-hidden="true">→</span>
                            </button>
                        )}

                        {/* <button
                        onClick={() => {
                            addModal(<ROSVideoModal />)
                        }}
                        type="button"
                        className="flex items-center space-x-1 rounded bg-orange-200 my-2 p-2 text-sm font-bold">
                        <span>Learn More About ROS Offers</span>
                        <PlayIcon className="h-5 w-5" />
                    </button> */}
                    </div>
                )}

            {localErrors &&
                localErrors.map(({ message }, idx) => (
                    <div key={message + idx}>{message}</div>
                ))}

            {files.length > 0 && !isServiceProductBySlug(product) && (
                <UploadStats product={product} files={csvFiles} />
            )}

            {!hideListAddOns && totalInvalids > 0 && (
                <div className="p-2 my-4 rounded">
                    {' '}
                    <ShowField
                        shouldReset={shouldReset}
                        item={undelivarblesField}
                        setValue={setValue}
                        getValues={getValues}
                        watch={watch}
                        errors={errors}
                    />
                </div>
            )}

            {!hideListAddOns && showReturneMailAddOn && (
                <div className="p-2 my-4 rounded">
                    <ShowField
                        shouldReset={shouldReset}
                        item={returnMailAddOnField}
                        setValue={setValue}
                        getValues={getValues}
                        watch={watch}
                        errors={errors}
                    />
                </div>
            )}
        </>
    )
})

MappingFileUpload.displayName = 'FileUpload'

export { MappingFileUpload }
